<template>
  <validation-observer ref="formObserver" v-slot="{ passes }" tag="div">
    <h2 class="mb-3">
      Step 2: Project Registration
    </h2>
    <div class="mb-8 bg-primary-300 py-3 px-5">
      <p class="text-lg font-bold mb-3">
        NEW AWI STANDARDS ALERT!
      </p>
      <p class="mb-2">
        If this project was BID after <strong><em>3/15/2019</em></strong>, unless otherwise specified, the new ANSI/AWI 0620 – 2018 Finish Carpentry/Installation Standards will apply for all Installation.
      </p>
      <p class="mb-2">
        If this project was BID after <strong><em>6/1/2020</em></strong>, unless otherwise specified, BOTH the new ANSI/AWI 0620 – 2018 Finish Carpentry/Installation Standards, AND, the new ANSI/AWI 0641-2019 Architectural Wood Casework Standards will apply for casework Fabrication and all Installation.
      </p>
      <hr class="md:w-1/4">
      <p class="mb-2">
        This will require review of <a href="https://www.awinet.org/standards/casework/introduction" target="_blank" rel="noopener noreferrer">casework</a> testing requirements.
      </p>
      <p class="mb-2">
        For further information, please see the referenced <a href="https://www.awinet.org/standards/standards-overview" target="_blank" rel="noopener noreferrer">Standards available on the AWI website.</a>
      </p>
    </div>
    <form @submit.prevent="passes(goToNextPage)">
      <div class="flex flex-wrap -mx-5 mb-4 md:mb-0">
        <d-field label="Project City" class="w-full md:w-1/3 px-5">
          <d-input v-model="projectCity" aria-label="Project city" required />
        </d-field>
        <d-field label="Project Country" class="w-full md:w-1/3 px-5">
          <d-select v-model="projectCountry" expanded required>
            <option value="" disabled>
              Select a Country
            </option>
            <option v-for="country in countryOptions" :key="country.name" :value="country.name">
              {{ country.name }}
            </option>
          </d-select>
        </d-field>
        <d-field label="Project State/Province" class="w-full md:w-1/3 px-5">
          <d-select v-model="projectStateProvince" expanded required>
            <option value="" disabled>
              Select a State
            </option>
            <option v-for="state in availableStateOptions" :key="state.name" :value="state.abbreviation">
              {{ state.name }}
            </option>
          </d-select>
        </d-field>
      </div>

      <div class="flex flex-wrap -mx-5 mb-4 md:mb-0">
        <d-field label="Standards" class="w-full md:w-1/2 px-5">
          <d-select v-model="projectStandards" required expanded>
            <option value="" disabled>
              Please select an option
            </option>
            <option value="AWI Standards, Current Edition">
              AWI Standards: Current Edition
            </option>
            <option value="AWS, Edition 2">
              Architectural Woodwork Standards: Edition 2 [2019]
            </option>
          </d-select>
        </d-field>
        <d-field label="Status" class="w-full md:w-1/2 px-5">
          <d-select v-model="projectStatus" required expanded>
            <option value="" disabled>
              Please select an option
            </option>
            <option value="Architectural Design">
              Architectural Design
            </option>
            <option value="Construction (Not Woodwork)">
              Construction (Not Woodwork)
            </option>
            <option value="Bidding (Woodwork)">
              Bidding (Woodwork)
            </option>
            <option value="Pre-fabrication (Woodwork)">
              Pre-fabrication (Woodwork)
            </option>
            <option value="Shop Drawings (Woodwork)">
              Shop Drawings (Woodwork)
            </option>
            <option value="Fabrication (Woodwork)">
              Fabrication (Woodwork)
            </option>
            <option value="Installation (Woodwork)">
              Installation (Woodwork)
            </option>
          </d-select>
        </d-field>
      </div>

      <div class="flex flex-wrap -mx-5 mb-4 md:mb-5">
        <d-field label="Project Bid Date" class="w-full md:w-1/3 px-5" help-message="This date establishes which Standards version will be referenced at the time of inspection unless otherwise specified.">
          <d-datepicker v-model="projectBidDate" expanded placeholder="Click to select a date..." icon="calendar" editable />
        </d-field>
        <d-field label="Estimated Start of Fabrication" class="w-full md:w-1/3 px-5" help-message="Projects should be registered at minimum 2 weeks prior to the start of Fabrication. ">
          <d-datepicker v-model="projectEstimatedShopDrawingStart" expanded placeholder="Click to select a date..." icon="calendar" editable />
        </d-field>
        <d-field label="Estimated Completion Date" message="(For firms providing finishing or installation only)" class="w-full md:w-1/3 px-5" help-message="Specific to the millwork portion of the project. ">
          <d-datepicker v-model="projectCompletionDate" expanded placeholder="Click to select a date..." icon="calendar" editable />
        </d-field>
      </div>

      <div class="flex flex-wrap justify-center md:justify-end pt-5">
        <d-btn class="mx-5" @click="goToPrevPage">
          Prev <span class="hidden sm:block sm:ml-1">Step</span>
        </d-btn>
        <d-btn type="submit" class="mx-5 md:mx-0">
          Next <span class="hidden sm:block sm:ml-1">Step</span>
        </d-btn>
      </div>
    </form>
  </validation-observer>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex';
  import { mapFields } from '@resources/scripts/utilities/field.utilities';
  import { PROJECT_REGISTRATION_FORM } from '@resources/scripts/store/mutation-types';

  const { mapActions, mapGetters } = createNamespacedHelpers('projectRegistrationForm');

  export default {


    parent: true,

    computed: {
      ...mapFields({
        fields: ['projectCity', 'projectCountry', 'projectStateProvince', 'projectStandards', 'projectStatus', 'projectBidDate', 'projectEstimatedShopDrawingStart', 'projectCompletionDate'],
        module: 'projectRegistrationForm',
        base: 'formFields',
        mutation: PROJECT_REGISTRATION_FORM.FORM_FIELDS,
      }),

      ...mapGetters([
        'countryOptions',
        'stateProvinceOptions',
      ]),

      availableStateOptions() {
        return this.stateProvinceOptions(this.projectCountry);
      },
    },

    watch: {
      projectCountry() {
        this.projectStateProvince = '';
      },
    },

    methods: {
      ...mapActions([
        'goToPrevPage',
        'goToNextPage',
      ]),
    },
  };
</script>